import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  LabeledTextArea,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createFleetMaintenanceType, updateFleetMaintenanceType } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceType, FleetMaintenanceTypeRequest } from '../../../types/fleet';

type Props = Readonly<{
  maintenanceType?: FleetMaintenanceType;
  onCancel: () => void;
  afterSave: () => void;
}>;

export function FleetMaintenanceTypeForm({ maintenanceType, onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [editingMaintenanceType, setEditingMaintenanceType] = useState<FleetMaintenanceTypeRequest>({
    clientId: apiParams?.clientId ?? '',
    subClientId: apiParams?.subClientId ?? '',
    description: '',
    value: '',
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (maintenanceType && apiParams) {
      setEditingMaintenanceType({
        clientId: maintenanceType.client?._id ?? apiParams.clientId,
        subClientId: apiParams.subClientId,
        description: maintenanceType.description,
        value: maintenanceType.value,
      });
    }
  }, [maintenanceType, apiParams]);

  const canSubmit = useMemo(() => {
    return !!editingMaintenanceType.value && !!editingMaintenanceType.description;
  }, [editingMaintenanceType]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetMaintenanceType) => {
    setEditingMaintenanceType((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = maintenanceType
      ? await updateFleetMaintenanceType(maintenanceType._id, editingMaintenanceType)
      : await createFleetMaintenanceType(editingMaintenanceType);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        maintenanceType
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.maintenanceType, editingMaintenanceType.value)
          : FEEDBACK.created(FEEDBACK_PREFIXES.maintenanceType),
      );
    } else {
      showFailFeedback(
        error || maintenanceType
          ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.maintenanceType, editingMaintenanceType.value)
          : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.maintenanceType),
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <LabeledInput
          label="Nombre:"
          value={editingMaintenanceType?.value}
          onChange={(value) => onInputChange(value, 'value')}
          required
        />
        {maintenanceType?.slug && (
          <LabeledInput
            label="Slug:"
            value={maintenanceType.slug}
            readonly
            caption="Este valor no se puede editar"
          />
        )}
        <LabeledTextArea
          label="Detalles:"
          value={editingMaintenanceType.description}
          onChangeValue={(value) => onInputChange(value, 'description')}
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {maintenanceType?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
